<template>
  <div>
    <div v-if="displayType === 'long'">
      <p>
        3-year trailing returns and volatilities presented in this analysis are hypothetical in nature. They do not reflect actual investment results and are not guarantees of future results.
      </p>
      <p>
        3-year trailing returns and volatilities are presented as of {{ lastDayOfMonth }}. The chart displays month-end, annualized returns, assuming monthly portfolio rebalancing to the current allocation. Volatility is based off of historical returns data for each security in the portfolio. Returns data are adjusted for splits dividends, and assume reinvestment of dividends and capital gains. Returns data do not account for taxes, loads, sales charges, trading commissions, short-term trading fees, management fees, or advisory fees.
      </p>
      <p>
        Coverage includes Equities, Mutual Funds, ETFs, and Cash. 
        <template v-if="portfolio">
          These calculations cover {{ portfolio.trailing_returns_monthly_rebalance?.[1]?.trailing_3_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }} of {{ portfolioName }}.
        </template>
        <template v-if="benchmark">
          These calculations cover {{ benchmark.trailing_returns_monthly_rebalance?.[1]?.trailing_3_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }} of {{ benchmarkName }}.
        </template>
        <template v-if="optimizedPortfolio">
          These calculations cover {{ optimizedPortfolio.trailing_returns_monthly_rebalance?.[1]?.trailing_3_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }} of {{ optimizedPortfolioName }}.
        </template>
        Securities that do not have at least 3 years of price data are excluded from this chart.
      </p>
      <p>
        Volatilities are calculated by taking the standard deviation of daily portfolio returns. Returns are calculated by multiplying the returns of each covered security in the portfolio by that security's current allocation in the portfolio using the following formula:
        Rp,n = Sum(Wi,n x Ri,n) from i=1 to m, where Rp,n is the portfolio returns at month n, Wi,n is the weight of asset i in month n, Ri,n is the monthly return of asset i in month n, and there are m securities with returns in month n. We assume Cash has 0 volatility for all monthly periods.
      </p>
      <p>
        When returns are not available for a holding (e.g. the security did not exist during historical period being reported), the remaining portfolio holdings are re-weighted to maintain consistent ratios using the following formula: Wnew,i,n = Wcurrent,i x (SUMcovered(Wcurrent,i)) / (1-(SUMnot_covered(Wcurrent,i))), where Wnew,i,n is the adjusted weight of security i at day n, SUMnot_covered is the sum over all securities that do not have daily returns available at day n, SUMcovered is the sum over all the securities that do have daily returns available at day n, and Wcurrent is the current weight of security i.
      </p>
      <p>
        Trailing volatilities are annualized by multiplying the standard deviation of daily portfolio returns by the square root of 252.
      </p>
      <p>
        Trailing 3-year returns are calculated by applying the following formula to monthly returns: Rp = (((1+Rp1) x (1+Rp2) x ... x (1+Rn))^1/36) - 1 where Rp is the total portfolio return, and there are 36 months in the 3-year period.
      </p>
      <p>
        Trailing returns are annualized using the following formula: Rp,annualized = ((1 + Rp)^(12/36)) - 1, where Rp is the total portfolio return, and there are 36 months in the 3-year period.
      </p>
    </div>

    <p v-else>
      3-year trailing returns and volatilities are presented as of {{ lastDayOfMonth }}.
      The chart displays month-end, annualized returns, assuming monthly portfolio rebalancing to the current allocation.
      Volatility is based off of historical returns data for each security in the portfolio.
      Returns data are adjusted for splits dividends, and assume reinvestment of dividends and capital gains.
      Returns data do not account for taxes, loads, sales charges, trading commissions, short-term trading fees, management fees, or advisory fees.
      Coverage includes Equities, Mutual Funds, ETFs, and Cash.
      <template v-if="portfolio">
        These calculations cover
        {{ portfolio.trailing_returns_monthly_rebalance?.[1]?.trailing_3_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }}
        of {{ portfolioName }}.
      </template>
      <template v-if="benchmark">
        These calculations cover
        {{ benchmark.trailing_returns_monthly_rebalance?.[1]?.trailing_3_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }}
        of {{ benchmarkName }}.
      </template>
      <template v-if="optimizedPortfolio">
        These calculations cover
        {{ optimizedPortfolio.trailing_returns_monthly_rebalance?.[1]?.trailing_3_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }}
        of {{ optimizedPortfolioName }}.
      </template>
      See full methodology below.
    </p>
  </div>
</template>


<script>
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';

export default {
  name: 'RiskReturnsMethodology',
  props: ['displayType', 'portfolio', 'benchmark', 'optimizedPortfolio'],
  computed: {
    ...mapStores(useCheckupStore),
    lastDayOfMonth() {
      const today = new Date();

      // 1. If there's a created date and it's in a different month/year than today, use it:
      if (this.checkupStore.portfolio_checkup_created_date) {
        const createdDate = new Date(this.checkupStore.portfolio_checkup_created_date);
        if (!isNaN(createdDate)) {
          // Only use createdDate if its month or year differs from today’s
          if (
            createdDate.getMonth() !== today.getMonth() ||
            createdDate.getFullYear() !== today.getFullYear()
          ) {
            // Move to the “zero‐th” day of the next month → last day of createdDate’s month
            const temp = new Date(createdDate);
            temp.setDate(0);

            const day = temp.getDate();
            const month = temp.getMonth() + 1;
            const year = temp.getFullYear();
            return `${month}/${day}/${year}`;
          }
        }
      }

      // 2. Otherwise, try to pull “1_yr_returns” from portfolio → benchmark → optimizedPortfolio:
      const oneYr =
        this.portfolio?.trailing_returns_monthly_rebalance?.[3]?.['1_yr_returns'] ||
        this.benchmark?.trailing_returns_monthly_rebalance?.[3]?.['1_yr_returns'] ||
        this.optimizedPortfolio?.trailing_returns_monthly_rebalance?.[3]?.['1_yr_returns'];

      if (oneYr && Object.keys(oneYr).length) {
        // Keys are in “YYYY-MM-DD” form; pick the latest and parse it:
        const dates = Object.keys(oneYr);
        const lastDateKey = dates.reduce((a, b) => (a > b ? a : b), dates[0]);
        const parsed = new Date(lastDateKey);
        const m = parsed.getMonth() + 1;
        const d = parsed.getDate();
        const y = parsed.getFullYear();
        return `${m}/${d}/${y}`;
      }

      // 3. If neither of the above yields a date, fall back to the last day of the previous month:
      today.setDate(0);
      const mm = today.getMonth() + 1;
      const dd = today.getDate();
      const yy = today.getFullYear();
      return `${mm}/${dd}/${yy}`;
    },
    portfolioName() {
      const name = this.checkupStore.portfolio?.name;
      return name || 'Portfolio';
    },
    benchmarkName() {
      const name = this.benchmark?.name;
      return name || 'Benchmark';
    },
    optimizedPortfolioName() {
      const name = this.checkupStore.optimizedPortfolio?.name;
      return name || 'Optimized Portfolio';
    },
  },
};
</script>
